import { ActionReducerMapBuilder, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRequestState } from "../request.state.interface";
import getQuizHandler, { IQuizTheme } from "./get-quiz.handler";
import startQuizHandler from "./start-quiz.handler";
import quizAnswerHandler from "./quiz-answer.handler";
import getQuizThemesHandler from "./get-quiz-themes.handler";
import requestQuizQuestionHandler from "./request-quiz-question.handler";
import finishQuizHandler from "./finish-quiz.handler";
import abortQuizHandler from "./abort-quiz.handler";
import { ITicketsBalances } from "../../model/user-info.interface";

export enum QuizQuestionComplexity {
    None = 0,
    Base = 1,
    Advanced = 2,
    Expert = 3
}

export const QuizQuestionComplexityNames = {
    [QuizQuestionComplexity.None]: 'Отсутствует',
    [QuizQuestionComplexity.Base]: 'Базовый',
    [QuizQuestionComplexity.Advanced]: 'Продвинутый',
    [QuizQuestionComplexity.Expert]: 'Экспертный'
} as const;

export enum QuizStatus {
    Created = 1,
    Inited = 2,
    WaitingAnswer = 3,
    WaitingQuestionRequest = 4,
    WaitingFinishRequest = 5,
    Finished = 6,
    Expired = 7,
    Aborted = 8,
}

export interface IQuizAnswer {
    index: number;
    text: string;
}

export interface IQuizQuestion {
    complexity: QuizQuestionComplexity;
    questionText: string;
    answers: IQuizAnswer[];
    reward: number;
}

export interface IQuizCurrentState {
    id: number;
    stageNumber: number;
    question: IQuizQuestion;
    now: number;
    cooldown: number;
    status: QuizStatus;
    totalQuestionsCount: number;
    rightAsnwersCount: number;
    totalReward: number;
}

export interface IAnswerResult {
    isRightAnswer: boolean;
    reward: number;
    answerText: string;
}

type TicketsRewards = {
    [K in keyof typeof QuizQuestionComplexity]?: Partial<ITicketsBalances>;
};

export interface IQuizState extends IRequestState {
    themes: IQuizTheme[];
    ticketsRewards: TicketsRewards;

    ignoreThemeIds: number[];
    quiz: IQuizCurrentState | null;
    answerResult: IAnswerResult | null;
    isExpired: boolean;
    deltaTime: number;
};

const initialState: IQuizState = {
    themes: [],
    ticketsRewards: {},

    ignoreThemeIds: [],
    quiz: null,
    answerResult: null,
    isExpired: false,
    deltaTime: 0,

    loading: false,
    error: "",
};

const quizSlice = createSlice({
    name: 'quiz',
    initialState,
    reducers: {
        setQuizExpired: (state, action: PayloadAction<boolean>) => {
            state.isExpired = action.payload;
        }
    },
    extraReducers: (builder: ActionReducerMapBuilder<IQuizState>) => {
        getQuizHandler(builder);
        startQuizHandler(builder);
        quizAnswerHandler(builder);
        getQuizThemesHandler(builder);
        requestQuizQuestionHandler(builder);
        finishQuizHandler(builder);
        abortQuizHandler(builder);
    },
});

export default quizSlice.reducer;
export const { setQuizExpired } = quizSlice.actions;